import { get } from 'svelte/store';
//Helpers
import { real_id } from 'src/helpers/interface_helpers';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { status_configurator } from 'src/helpers/device_data_helpers';
import { convert, consumption_add_total } from 'src/helpers/utilities';
import {
	getCustomizeLabelList,
	normalizeParams,
} from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { consumption_device } from 'src/stores/features/consumption';
//Types
import type { Room } from 'src/types/Room';
import type { Device } from 'src/types/Device';

/**
 * Builds the labels shown for a room in the room list.
 * Always includes the aggregated room power label and, when the room's main sensor is online,
 * appends the allowed status labels from that sensor.
 *
 * @param {Room} room Room used to collect power consumption and resolve the configured main sensor.
 * @param {any} devicesStatus Current devices status map, keyed by real device id.
 * @returns {Array<any>} Ordered list of labels for the room card/list item.
 */
export const getRoomLabelList = (room: Room, devicesStatus: any, consumptionDevice?: any) => {
	const consObjList = getConsObjList(room, consumptionDevice);
	const power = {
		value: convert(consumption_add_total(consObjList, 'in_power'), 'W'),
		icon: 'fas fa-bolt',
		name: 'room power',
	};
	const allDevices = get(devices);
	const mainSensor = room?.main_sensor as string;
	const mainDevice = mainSensor && allDevices?.[mainSensor];
	const mainStatus = mainSensor && devicesStatus?.[real_id(mainSensor)];
	const mainSettings = mainSensor && devices_settings.getByRealId(mainSensor);
	const canUseMainSensor =
		room?.id && mainSensor && mainDevice?.room_id == room.id && mainStatus?.online;
	if (!canUseMainSensor) return [power];

	const statuses = status_configurator(mainStatus, mainDevice, mainSettings);
	let labelList = getCustomizeLabelList(mainDevice, statuses);
	if (isVirtualDevice(mainDevice?.type)) {
		labelList = labelList.filter((i) => !i?.['hasButton']);
	} else {
		labelList = labelList.filter(filterLabels);
	}
	return labelList.length > 0 ? [power, ...labelList] : [power];
};

/**
 * Gets the main label for the room's configured main sensor.
 * Returns an empty list when the room has no usable online main sensor.
 *
 * @param {Room} room Room containing the main sensor configuration.
 * @param {any} devicesStatus Current devices status map, keyed by real device id.
 * @returns {Array<any>} Main status label candidates for the room.
 */
export const getRoomMainLabel = (room: Room, devicesStatus) => {
	const allDevices = get(devices);
	const mainSensor = room?.main_sensor as string;
	const mainDevice = mainSensor && allDevices?.[mainSensor];
	const mainStatus = mainSensor && devicesStatus?.[real_id(mainSensor)];
	const mainSettings = mainSensor && devices_settings.getByRealId(mainSensor);
	const canUseMainSensor =
		room?.id && mainSensor && mainDevice?.room_id == room.id && mainStatus?.online;
	if (!canUseMainSensor) return [];

	const statuses = status_configurator(mainStatus, mainDevice, mainSettings);
	return getCustomMainLabel(statuses, mainDevice);
};

/**
 * Filters out status labels that should not be displayed as secondary room labels.
 *
 * @param {any} label Status label generated from the main sensor.
 * @returns {boolean} true when the label can be shown in the room list.
 */
const filterLabels = (label: any) => {
	return (
		!label.hideLabel &&
		label.name !== 'timer' &&
		label.name !== 'schedule' &&
		label.name !== 'sleeping' &&
		label.name !== 'power' &&
		label.name !== 'voltage' &&
		label.name !== 'last' &&
		label.name !== 'last report' &&
		label.name !== 'click combinations'
	);
};

/**
 * Collects consumption objects for devices assigned to the provided room.
 *
 * @param {Room} room Room whose devices should be included.
 * @returns {Record<string, any>} Consumption data keyed by device id.
 */
const getConsObjList = (room: Room, consumptionDevice?: any) => {
	const allDevices = get(devices);
	const consData = consumptionDevice ?? get(consumption_device) ?? {};
	return Object.values(allDevices)
		.filter((e: Device) => e?.room_id == room?.id)
		.reduce((filtered, v: Device) => {
			if (typeof consData[v.id] !== 'undefined') {
				filtered[v.id] = { ...consData[v.id] };
			}
			return filtered;
		}, {});
};

/**
 * Normalizes a status label parameter into an array.
 *
 * @param {any} statusLabelParam Status label parameter as an array, object, or empty value.
 * @returns {Array<any>} Normalized list of status label parameter entries.
 */
const normalizeStatusLabelParam = (statusLabelParam) => {
	if (!statusLabelParam) return [];
	return Array.isArray(statusLabelParam) ? statusLabelParam : Object.values(statusLabelParam);
};

/**
 * Resolves the custom main label for a device.
 * If the device has a main label override in statusLabelParam, it uses that label name;
 * otherwise it falls back to statuses marked as main labels.
 *
 * @param {Array<any>} statuses Available status labels for the device.
 * @param {Device} device Device whose label settings are used.
 * @returns {Array<any>} Matching main label statuses.
 */
const getCustomMainLabel = (statuses, device: Device) => {
	const statusLabelParam = normalizeParams(device?.statusLabelParam);
	const mainParam = normalizeStatusLabelParam(statusLabelParam).find((i: any) => i?.m)?.n;
	return mainParam
		? statuses.filter((i) => i?.name === mainParam)
		: statuses.filter((i) => i?.main === true || i?.mainLabel === true);
};
